import { AlertTriangle, Sparkles } from 'lucide-react';
import type { VsKind, VsOutcome, VsPolicy } from '@/lib/api';
import { cn } from '@/lib/utils';
import {
  KIND_LABEL,
  dailySeverity,
  swatchClass,
  weekBoxClass,
  weekSeverity,
} from '@/lib/vsSeverity';

export interface VsWeekDay {
  dayIndex: number;
  scoreTenthsM: number | null;
  dayTwentiethTenthsM: number | null;
  dailyWin: boolean;
  rank?: number | null;
  totalPlayers?: number;
  othersAvgTenthsM?: number | null;
}

export interface VsWeekBoxProps {
  weekLabel: string;
  meta?: string | null;
  notes?: string | null;
  kind: VsKind;
  outcome: VsOutcome | null;
  policy: VsPolicy;
  weekScoreM: number | null;
  twentiethM: number | null;
  days: VsWeekDay[] | null;
  compact?: boolean;
  rank?: number | null;
  totalPlayers?: number;
  othersAvgM?: number | null;
}

const KIND_CHIP_CLASS: Record<VsKind, string> = {
  push: 'bg-indigo-600/80 text-indigo-50 border-indigo-400/60',
  soft_push: 'bg-amber-600/80 text-amber-50 border-amber-400/60',
  save: 'bg-slate-700/80 text-slate-100 border-slate-500/60',
  aborted_push:
    'bg-rose-800/80 text-rose-50 border-rose-400/60 border-dashed',
};

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

function formatTenthsM(t: number | null): string {
  if (t == null) return '—';
  return (t / 10).toFixed(1);
}

/**
 * Reusable VS week visualization. Renders a bordered box (color = kind,
 * ring = outcome) around either six day cells (when daily data exists)
 * or one week-score cell (Alt-Wochen fallback).
 */
export default function VsWeekBox({
  weekLabel,
  meta,
  notes,
  kind,
  outcome,
  policy,
  weekScoreM,
  twentiethM,
  days,
  compact = false,
  rank,
  totalPlayers,
  othersAvgM,
}: VsWeekBoxProps) {
  const weekSev =
    weekScoreM == null
      ? 'none'
      : weekSeverity(weekScoreM, kind, twentiethM, policy);

  return (
    <div className={cn('rounded-lg p-2', weekBoxClass(kind, outcome))}>
      {!compact && (
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2 px-1 text-xs">
          <div className="flex min-w-0 items-center gap-2">
            <span
              className={cn(
                'inline-flex shrink-0 items-center rounded border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider',
                KIND_CHIP_CLASS[kind],
              )}
            >
              {KIND_LABEL[kind]}
            </span>
            <div className="min-w-0 truncate text-slate-300">
              <span className="font-medium">{weekLabel}</span>
              {meta ? (
                <span className="ml-1 text-slate-500">· {meta}</span>
              ) : null}
            </div>
          </div>
          {outcome && (
            <span
              className={cn(
                'rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider',
                outcome === 'win'
                  ? 'bg-emerald-900/60 text-emerald-200'
                  : 'bg-red-900/60 text-red-200',
              )}
            >
              {outcome}
            </span>
          )}
        </div>
      )}
      {!compact && notes && notes.trim() !== '' && (
        <div className="mb-2 whitespace-pre-wrap rounded border border-slate-800 bg-slate-900/60 px-2 py-1 text-[11px] italic text-slate-300">
          {notes}
        </div>
      )}
      {!compact && (rank != null || othersAvgM != null) && (
        <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 px-1 text-[11px] text-slate-400">
          {rank != null && totalPlayers != null && totalPlayers > 0 && (
            <span>
              Rank{' '}
              <span className="font-semibold tabular-nums text-slate-200">
                {rank}
              </span>
              <span className="text-slate-500">/{totalPlayers}</span>
            </span>
          )}
          {othersAvgM != null && (
            <span>
              Others avg{' '}
              <span className="font-semibold tabular-nums text-slate-200">
                {othersAvgM.toFixed(0)}
              </span>{' '}
              M
            </span>
          )}
        </div>
      )}
      {days ? (
        <div
          className={cn(
            'items-stretch gap-1',
            compact ? 'flex flex-wrap' : 'grid grid-cols-7',
          )}
        >
          {days.map((d) => {
            const sev =
              d.scoreTenthsM == null
                ? 'none'
                : dailySeverity(
                    d.scoreTenthsM,
                    kind,
                    d.dayTwentiethTenthsM,
                    policy,
                  );
            return (
              <div
                key={d.dayIndex}
                title={
                  `${DAY_LABELS[d.dayIndex]}: ${formatTenthsM(d.scoreTenthsM)} M` +
                  (d.rank != null && d.totalPlayers
                    ? ` · rank ${d.rank}/${d.totalPlayers}`
                    : '') +
                  (d.othersAvgTenthsM != null
                    ? ` · others avg ${formatTenthsM(d.othersAvgTenthsM)} M`
                    : '') +
                  (d.dayTwentiethTenthsM != null
                    ? ` · cut ${formatTenthsM(d.dayTwentiethTenthsM)} M`
                    : '') +
                  (d.dailyWin ? ' · day winner' : '')
                }
                className={cn(
                  'relative flex flex-col items-center justify-center rounded text-[10px] font-semibold leading-none',
                  compact ? 'h-6 w-6' : 'h-16 w-full gap-0.5 py-1',
                  swatchClass[sev],
                )}
              >
                {d.dailyWin && (
                  <Sparkles
                    size={compact ? 10 : 12}
                    strokeWidth={2.5}
                    className="absolute right-0.5 top-0.5 text-yellow-100 drop-shadow-[0_0_2px_rgba(0,0,0,0.9)]"
                  />
                )}
                {sev === 'red-absolute' && !d.dailyWin && (
                  <AlertTriangle
                    size={compact ? 10 : 12}
                    strokeWidth={3}
                    className="absolute right-0.5 top-0.5 text-white"
                  />
                )}
                <span className="text-[9px] font-normal text-current opacity-70">
                  {DAY_LABELS[d.dayIndex]}
                </span>
                <span className="tabular-nums">
                  {formatTenthsM(d.scoreTenthsM)}
                </span>
                {!compact && (d.rank != null || d.othersAvgTenthsM != null) && (
                  <span className="mt-0.5 flex items-center gap-1 text-[8px] font-normal tabular-nums opacity-70">
                    {d.rank != null && <span>#{d.rank}</span>}
                    {d.othersAvgTenthsM != null && (
                      <span>⌀ {formatTenthsM(d.othersAvgTenthsM)}</span>
                    )}
                  </span>
                )}
              </div>
            );
          })}
          {(() => {
            const scored = days.filter((d) => d.scoreTenthsM != null);
            const summed = scored.reduce(
              (acc, d) => acc + (d.scoreTenthsM as number),
              0,
            );
            const sumM = scored.length > 0 ? summed / 10 : null;
            const avgM =
              scored.length > 0 ? summed / 10 / scored.length : null;
            return (
              <div
                title={
                  sumM != null
                    ? `Week sum: ${sumM.toFixed(1)} M · avg/day ${avgM!.toFixed(1)} M`
                    : 'No scores'
                }
                className={cn(
                  'flex flex-col items-center justify-center rounded bg-slate-800/80 text-slate-100 text-[10px] font-semibold leading-none',
                  compact ? 'h-6 min-w-[3rem] px-1' : 'h-16 w-full gap-0.5 px-1 py-1',
                )}
              >
                <span className="mt-0.5 text-[9px] font-normal opacity-70">Σ</span>
                <span className="tabular-nums">
                  {sumM != null ? sumM.toFixed(1) : '—'}
                </span>
                {!compact && (
                  <span className="mt-0.5 text-[8px] font-normal tabular-nums opacity-60">
                    ⌀ {avgM != null ? avgM.toFixed(1) : '—'}
                  </span>
                )}
              </div>
            );
          })()}
        </div>
      ) : (
        <div
          title={
            weekScoreM != null
              ? `Week total: ${weekScoreM.toLocaleString()} M${
                  twentiethM != null
                    ? ` · top ${policy.topRank} cut ${twentiethM.toLocaleString()} M`
                    : ''
                }`
              : 'No score'
          }
          className={cn(
            'flex items-center justify-center rounded font-semibold tabular-nums',
            compact ? 'h-6 min-w-[3rem] px-2 text-[10px]' : 'h-12 min-w-[6rem] px-3 text-sm',
            swatchClass[weekSev],
          )}
        >
          {weekScoreM != null ? weekScoreM.toLocaleString() : '—'}
        </div>
      )}
    </div>
  );
}
